import request from '@/utils/request';

/**
 * 获取船舶黑白名单列表 (分页)
 * @param {Object} params 查询参数
 * @param {number} [params.bridgeId] 桥梁ID
 * @param {number} [params.pageNum] 页码
 * @param {number} [params.pageSize] 每页数量
 * @param {string} [params.mmsi] 船舶MMSI
 * @param {number} [params.listType] 名单类型：0-白名单，1-黑名单
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     list: Array<{
 *       id: number,
 *       bridgeId: number,
 *       mmsi: string,
 *       listType: number,
 *       reason: string,
 *       vesselName: string,
 *       cnName: string,
 *       vesselType: string,
 *       vesselTypeName: string,
 *       createTime: string,
 *       updateTime: string
 *     }>,
 *     total: number,
 *     pageNum: number,
 *     pageSize: number
 *   }
 * }>} 分页的船舶黑白名单列表
 */
export function getBlacklistList(params) {
  return request({
    url: '/vessel/blacklist/list',
    method: 'get',
    params
  });
}

/**
 * 获取指定桥梁的所有黑白名单船舶
 * @param {number} bridgeId 桥梁ID
 * @param {number} [listType] 名单类型：0-白名单，1-黑名单
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: Array<{
 *     id: number,
 *     bridgeId: number,
 *     mmsi: string,
 *     listType: number,
 *     reason: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     vesselTypeName: string,
 *     createTime: string,
 *     updateTime: string
 *   }>
 * }>} 船舶黑白名单列表
 */
export function getVesselBlacklistByBridgeId(bridgeId, listType) {
  return request({
    url: `/vessel/blacklist/bridge/${bridgeId}`,
    method: 'get',
    params: { listType }
  });
}

/**
 * 根据ID获取船舶黑白名单详情
 * @param {number} id 记录ID
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeId: number,
 *     mmsi: string,
 *     listType: number,
 *     reason: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     vesselTypeName: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 船舶黑白名单详情
 */
export function getBlacklistById(id) {
  return request({
    url: `/vessel/blacklist/${id}`,
    method: 'get'
  });
}

/**
 * 添加船舶到黑白名单
 * @param {Object} data 黑白名单数据
 * @param {number} data.bridgeId 桥梁ID
 * @param {string} data.mmsi 船舶MMSI号
 * @param {number} data.listType 名单类型：0-白名单，1-黑名单
 * @param {string} data.reason 加入名单原因
 * @param {string} [data.vesselName] 船舶名称
 * @param {string} [data.cnName] 船舶中文名称
 * @param {string} [data.vesselType] 船舶类型
 * @param {string} [data.vesselTypeName] 船舶类型中文名称
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeId: number,
 *     mmsi: string,
 *     listType: number,
 *     reason: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     vesselTypeName: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 创建的黑白名单记录
 */
export function createBlacklist(data) {
  return request({
    url: '/vessel/blacklist',
    method: 'post',
    data
  });
}

/**
 * 批量添加船舶到黑白名单
 * @param {Array<{
 *   bridgeId: number,
 *   mmsi: string,
 *   listType: number,
 *   reason: string,
 *   vesselName?: string,
 *   cnName?: string,
 *   vesselType?: string,
 *   vesselTypeName?: string
 * }>} data 黑白名单数据数组
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: number
 * }>} 成功添加的记录数
 */
export function batchAddToBlacklist(data) {
  return request({
    url: '/vessel/blacklist/batch',
    method: 'post',
    data
  });
}

/**
 * 更新船舶黑白名单信息
 * @param {number} id 记录ID
 * @param {Object} data 更新数据
 * @param {number} data.bridgeId 桥梁ID
 * @param {string} data.mmsi 船舶MMSI号
 * @param {number} data.listType 名单类型：0-白名单，1-黑名单
 * @param {string} data.reason 加入名单原因
 * @param {string} [data.vesselName] 船舶名称
 * @param {string} [data.cnName] 船舶中文名称
 * @param {string} [data.vesselType] 船舶类型
 * @param {string} [data.vesselTypeName] 船舶类型中文名称
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: {
 *     id: number,
 *     bridgeId: number,
 *     mmsi: string,
 *     listType: number,
 *     reason: string,
 *     vesselName: string,
 *     cnName: string,
 *     vesselType: string,
 *     vesselTypeName: string,
 *     createTime: string,
 *     updateTime: string
 *   }
 * }>} 更新后的记录
 */
export function updateBlacklist(id, data) {
  return request({
    url: `/vessel/blacklist/${id}`,
    method: 'put',
    data
  });
}

/**
 * 从黑白名单删除船舶
 * @param {number} id 记录ID
 * @returns {Promise<{
 *   code: number,
 *   message: string
 * }>} 删除结果
 */
export function deleteBlacklist(id) {
  return request({
    url: `/vessel/blacklist/${id}`,
    method: 'delete'
  });
}

/**
 * 检查船舶是否在指定名单中
 * @param {number} bridgeId 桥梁ID
 * @param {string} mmsi 船舶MMSI
 * @param {number} listType 名单类型：0-白名单，1-黑名单
 * @returns {Promise<{
 *   code: number,
 *   message: string,
 *   data: boolean
 * }>} 检查结果，true表示在名单中
 */
export function isVesselInBlacklist(bridgeId, mmsi, listType) {
  return request({
    url: '/vessel/blacklist/check',
    method: 'get',
    params: { bridgeId, mmsi, listType }
  });
} 